import { refs } from '../core/refs.js';
import { isLocalDevelopmentOrigin } from '../core/sandbox-origin.js';
import { Storage } from '../core/storage.js';
import {
  DEFAULT_DEVICE_PROFILE_ID,
  getDefaultDeviceProfile,
  getDeviceProfile,
  normalizeCustomPreviewFrame,
  profileToPreviewFrame,
  swapPreviewFrameOrientation
} from './preview-device-profiles.js';

let currentState = { mode: 'fit', presetId: 'fit', profileId: DEFAULT_DEVICE_PROFILE_ID, customFrame: null };
let previewPresetServices = {
  console: null
};
let pendingScaleFrame = 0;

const PRESETS = {
  mobile: { w: 375, h: 667, label: 'Mobile' },
  tablet: { w: 768, h: 1024, label: 'Tablet' },
  desktop: { w: 1200, h: 800, label: 'Desktop' }
};
const PRESET_BADGE_SPACE = 36;
const DEVICE_FRAME_CLASSES = [
  'device-frame-active',
  'device-mobile',
  'device-tablet',
  'device-desktop',
  'device-shell-active',
  'device-profile-frame',
  'device-custom-frame',
  'device-cutout-dynamic-island',
  'device-cutout-hole-punch',
  'device-cutout-none',
  'device-chrome-ios-safari-approx',
  'device-chrome-android-chrome-approx',
  'device-chrome-tablet-safari-approx',
  'device-chrome-android-tablet-approx',
  'device-chrome-custom',
  'device-chrome-none'
];

function cloneState(state = currentState) {
  return {
    mode: state.mode,
    presetId: state.presetId,
    profileId: state.profileId,
    customFrame: state.customFrame ? { ...state.customFrame, safeAreaInsets: { ...state.customFrame.safeAreaInsets } } : null
  };
}

function normalizeSavedState(value) {
  if (typeof value === 'string') {
    if (value === 'fit') return { mode: 'fit', presetId: 'fit', profileId: DEFAULT_DEVICE_PROFILE_ID, customFrame: null };
    if (PRESETS[value]) return { mode: 'preset', presetId: value, profileId: DEFAULT_DEVICE_PROFILE_ID, customFrame: null };
  }

  if (!value || typeof value !== 'object') {
    return { mode: 'fit', presetId: 'fit', profileId: DEFAULT_DEVICE_PROFILE_ID, customFrame: null };
  }

  if (value.mode === 'preset' && PRESETS[value.presetId]) {
    return { mode: 'preset', presetId: value.presetId, profileId: DEFAULT_DEVICE_PROFILE_ID, customFrame: null };
  }

  if (value.mode === 'device' && getDeviceProfile(value.profileId)) {
    return { mode: 'device', presetId: 'fit', profileId: value.profileId, customFrame: null };
  }

  if (value.mode === 'custom') {
    return {
      mode: 'custom',
      presetId: 'fit',
      profileId: DEFAULT_DEVICE_PROFILE_ID,
      customFrame: normalizeCustomPreviewFrame(value.customFrame || value)
    };
  }

  return { mode: 'fit', presetId: 'fit', profileId: DEFAULT_DEVICE_PROFILE_ID, customFrame: null };
}

function persistCurrentState() {
  Storage.setPreviewFrameState(cloneState());
}

function restoreCurrentState() {
  currentState = normalizeSavedState(Storage.getPreviewFrameState());
}

function targetFromState() {
  if (currentState.mode === 'preset' && PRESETS[currentState.presetId]) {
    const preset = PRESETS[currentState.presetId];
    return {
      mode: 'preset',
      className: `device-${currentState.presetId}`,
      width: preset.w,
      height: preset.h,
      label: `${preset.label} Viewport`,
      badgeLabel: `${preset.label} Viewport`,
      shell: false,
      safeAreaInsets: { top: 0, right: 0, bottom: 0, left: 0 },
      cutout: { type: 'none' },
      chromeModel: 'none',
      note: ''
    };
  }

  if (currentState.mode === 'device') {
    const profile = getDeviceProfile(currentState.profileId) || getDefaultDeviceProfile();
    return {
      ...profileToPreviewFrame(profile),
      mode: 'device',
      className: 'device-profile-frame',
      badgeLabel: profile.label,
      shell: true
    };
  }

  if (currentState.mode === 'custom' && currentState.customFrame) {
    return {
      ...normalizeCustomPreviewFrame(currentState.customFrame),
      mode: 'custom',
      className: 'device-custom-frame',
      badgeLabel: currentState.customFrame.label || 'Custom Screen',
      shell: true
    };
  }

  return null;
}

function removeDeviceOverlay(wrapper) {
  const overlay = wrapper.querySelector(':scope > .preview-device-overlay');
  if (overlay) overlay.remove();
}

function createChromeElement(tagName, className, text = '') {
  const element = document.createElement(tagName);
  element.className = className;
  if (text) element.textContent = text;
  return element;
}

function appendBrowserIcon(parent, type) {
  parent.appendChild(createChromeElement('span', `preview-browser-icon preview-browser-icon-${type}`));
}

function createBrowserAddress(label = 'frontmyend.local') {
  const address = createChromeElement('div', 'preview-browser-address');
  address.append(
    createChromeElement('span', 'preview-browser-lock'),
    createChromeElement('span', 'preview-browser-host', label)
  );
  return address;
}

function createMobileStatusBar(platform = 'ios') {
  const status = createChromeElement('div', `preview-browser-status preview-browser-status-${platform}`);
  const indicators = createChromeElement('div', 'preview-browser-status-icons');
  indicators.append(
    createChromeElement('span', 'preview-browser-signal'),
    createChromeElement('span', 'preview-browser-wifi'),
    createChromeElement('span', 'preview-browser-battery')
  );
  status.append(
    createChromeElement('span', 'preview-browser-time', '9:41'),
    indicators
  );
  return status;
}

function createIconRow(types) {
  const row = createChromeElement('div', 'preview-browser-icon-row');
  types.forEach((type) => appendBrowserIcon(row, type));
  return row;
}

function createIosSafariChrome() {
  const chrome = createChromeElement('div', 'preview-browser-chrome preview-browser-chrome-ios');
  const toolbar = createChromeElement('div', 'preview-browser-toolbar preview-browser-toolbar-bottom preview-browser-toolbar-ios');
  toolbar.append(
    createBrowserAddress(),
    createIconRow(['back', 'forward', 'share', 'bookmarks', 'tabs'])
  );
  chrome.append(createMobileStatusBar('ios'), toolbar);
  return chrome;
}

function createAndroidChrome(model = 'phone') {
  const chrome = createChromeElement('div', `preview-browser-chrome preview-browser-chrome-android preview-browser-chrome-android-${model}`);
  const toolbar = createChromeElement('div', 'preview-browser-toolbar preview-browser-toolbar-top preview-browser-toolbar-android');
  toolbar.append(
    createBrowserAddress(),
    createIconRow(['tabs', 'menu'])
  );
  chrome.append(createMobileStatusBar('android'), toolbar);
  return chrome;
}

function createTabletSafariChrome() {
  const chrome = createChromeElement('div', 'preview-browser-chrome preview-browser-chrome-tablet-safari');
  const toolbar = createChromeElement('div', 'preview-browser-toolbar preview-browser-toolbar-top preview-browser-toolbar-tablet');
  toolbar.append(
    createIconRow(['back', 'forward', 'sidebar']),
    createBrowserAddress(),
    createIconRow(['share', 'plus', 'tabs'])
  );
  chrome.append(toolbar);
  return chrome;
}

function createBrowserChrome(target) {
  switch (target.chromeModel) {
    case 'ios-safari-approx':
      return createIosSafariChrome();
    case 'android-chrome-approx':
      return createAndroidChrome('phone');
    case 'tablet-safari-approx':
      return createTabletSafariChrome();
    case 'android-tablet-approx':
      return createAndroidChrome('tablet');
    default:
      return null;
  }
}

function syncDeviceOverlayChrome(overlay, target) {
  const chromeModel = target.chromeModel || 'none';
  const chromeKey = `${chromeModel}:${target.browserHint || ''}:${target.platform || ''}`;
  if (overlay.dataset.chromeKey === chromeKey) return;

  overlay.dataset.chromeModel = chromeModel;
  overlay.dataset.chromeKey = chromeKey;
  const chrome = createBrowserChrome(target);
  overlay.replaceChildren(...(chrome ? [chrome] : []));
}

function ensureDeviceOverlay(wrapper, target) {
  if (!target.shell) {
    removeDeviceOverlay(wrapper);
    return;
  }

  let overlay = wrapper.querySelector(':scope > .preview-device-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.className = 'preview-device-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    wrapper.appendChild(overlay);
  }
  syncDeviceOverlayChrome(overlay, target);
}

function setFrameCssVars(wrapper, target) {
  const safe = target.safeAreaInsets || {};
  wrapper.style.setProperty('--preview-safe-top', `${Math.max(0, safe.top || 0)}px`);
  wrapper.style.setProperty('--preview-safe-right', `${Math.max(0, safe.right || 0)}px`);
  wrapper.style.setProperty('--preview-safe-bottom', `${Math.max(0, safe.bottom || 0)}px`);
  wrapper.style.setProperty('--preview-safe-left', `${Math.max(0, safe.left || 0)}px`);
}

function clearFrameCssVars(wrapper) {
  wrapper.style.removeProperty('--preview-safe-top');
  wrapper.style.removeProperty('--preview-safe-right');
  wrapper.style.removeProperty('--preview-safe-bottom');
  wrapper.style.removeProperty('--preview-safe-left');
}

function clearFrameClasses(wrapper) {
  wrapper.classList.remove(...DEVICE_FRAME_CLASSES);
  delete wrapper.dataset.previewFrameMode;
  delete wrapper.dataset.previewFrameLabel;
}

function applyFrameClasses(wrapper, target) {
  clearFrameClasses(wrapper);
  wrapper.classList.add('device-frame-active');
  if (target.className) wrapper.classList.add(target.className);
  if (target.shell) wrapper.classList.add('device-shell-active');
  wrapper.classList.add(`device-cutout-${target.cutout?.type || 'none'}`);
  wrapper.classList.add(`device-chrome-${target.chromeModel || 'none'}`);
  wrapper.dataset.previewFrameMode = target.mode;
  wrapper.dataset.previewFrameLabel = target.label;
  setFrameCssVars(wrapper, target);
  ensureDeviceOverlay(wrapper, target);
}

function updatePresetBadge(container, target, finalScale) {
  let badge = document.getElementById('preview-preset-badge');
  if (!badge) {
    badge = document.createElement('div');
    badge.id = 'preview-preset-badge';
    badge.className = 'preview-preset-badge';
    container.appendChild(badge);
  }

  badge.style.display = 'block';
  badge.title = target.note || '';
  const zoomPercent = Math.round(finalScale * 100);
  const label = document.createElement('span');
  label.textContent = target.badgeLabel || target.label;
  const zoom = document.createElement('span');
  zoom.className = 'badge-zoom';
  zoom.textContent = `${zoomPercent}% Zoom`;
  badge.replaceChildren(label, zoom);
}

/**
 * Calculates the fitting scale ratio and updates the preview viewport container style.
 */
export function updatePreviewScale() {
  const wrapper = refs.previewWrapper;
  const iframe = refs.previewIframe;
  if (!wrapper || !iframe) return;

  const container = wrapper.parentElement;
  if (!container) return;

  const target = targetFromState();
  let badge = document.getElementById('preview-preset-badge');

  if (!target) {
    // Reset all viewport frame overrides
    container.classList.remove('has-preview-preset-badge');
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';
    wrapper.style.transform = '';
    wrapper.style.position = '';
    wrapper.style.left = '';
    wrapper.style.top = '';

    clearFrameClasses(wrapper);
    clearFrameCssVars(wrapper);
    removeDeviceOverlay(wrapper);

    if (badge) badge.style.display = 'none';
    return;
  }

  const targetW = target.width;
  const targetH = target.height;
  container.classList.add('has-preview-preset-badge');

  wrapper.style.position = 'absolute';
  wrapper.style.left = '50%';
  wrapper.style.width = `${targetW}px`;
  wrapper.style.height = `${targetH}px`;

  applyFrameClasses(wrapper, target);

  // Measure container dimensions
  const containerW = container.clientWidth;
  const containerH = Math.max(0, container.clientHeight - PRESET_BADGE_SPACE);
  wrapper.style.top = `${containerH / 2}px`;

  // Leave a small breathing margin and reserve the bottom strip for the badge.
  const scaleX = Math.max(0.05, (containerW - 16) / targetW);
  const scaleY = Math.max(0.05, (containerH - 16) / targetH);

  // Calculate best fitting scale ratio (never scale UP above 1.0)
  const finalScale = Math.min(1.0, Math.min(scaleX, scaleY));

  // Apply centered transform
  wrapper.style.transform = `translate(-50%, -50%) scale(${finalScale})`;

  // Update zoom watermark badge
  updatePresetBadge(container, target, finalScale);
}

function schedulePreviewScaleUpdate() {
  if (pendingScaleFrame) return;
  pendingScaleFrame = window.requestAnimationFrame(() => {
    pendingScaleFrame = 0;
    updatePreviewScale();
  });
}

/**
 * Triggers a simulated device viewport mode preset.
 */
export function setDevicePreset(mode) {
  if (mode !== 'fit' && !PRESETS[mode]) return;
  currentState = mode === 'fit'
    ? { mode: 'fit', presetId: 'fit', profileId: DEFAULT_DEVICE_PROFILE_ID, customFrame: null }
    : { mode: 'preset', presetId: mode, profileId: DEFAULT_DEVICE_PROFILE_ID, customFrame: null };
  persistCurrentState();
  updatePreviewScale();

  previewPresetServices.console?.system(`Switched Preview viewport to ${mode === 'fit' ? '100% Fit' : PRESETS[mode].label} mode.`);
}

export function setDeviceProfile(profileId) {
  const profile = getDeviceProfile(profileId);
  if (!profile) return false;

  currentState = { mode: 'device', presetId: 'fit', profileId: profile.id, customFrame: null };
  persistCurrentState();
  updatePreviewScale();
  previewPresetServices.console?.system(`${profile.label} enabled. This is a local layout simulation, not a real ${profile.browserHint} engine render.`);
  return true;
}

export function setCustomPreviewFrame(input) {
  const customFrame = normalizeCustomPreviewFrame(input);
  currentState = { mode: 'custom', presetId: 'fit', profileId: DEFAULT_DEVICE_PROFILE_ID, customFrame };
  persistCurrentState();
  updatePreviewScale();
  previewPresetServices.console?.system(`Custom Preview frame set to ${customFrame.width}x${customFrame.height} @ ${customFrame.dpr} DPR.`);
  return customFrame;
}

export function swapActivePreviewFrameOrientation() {
  const target = targetFromState();
  if (!target) return null;

  const swapped = swapPreviewFrameOrientation({
    label: target.label,
    width: target.width,
    height: target.height,
    dpr: target.dpr,
    safeAreaInsets: target.safeAreaInsets
  });
  setCustomPreviewFrame(swapped);
  return swapped;
}

export function getPreviewFrameState() {
  return cloneState();
}

export function getPreviewFrameTarget() {
  const target = targetFromState();
  return target ? { ...target, safeAreaInsets: { ...target.safeAreaInsets }, cutout: { ...target.cutout } } : null;
}

export function getActiveDevicePreset() {
  return currentState.mode === 'preset' ? currentState.presetId : 'fit';
}

export function handlePreviewContextMenuMessage(data) {
  const panel = refs.panelPreview;
  const iframe = refs.previewIframe;
  if (!panel || !iframe) return;

  const rect = iframe.getBoundingClientRect();

  // Find current scaling factor to accurately map coordinates inside the scaled frame
  const wrapper = refs.previewWrapper;
  let scale = 1.0;
  if (wrapper && wrapper.style.transform) {
    const match = wrapper.style.transform.match(/scale\(([^)]+)\)/);
    if (match && match[1]) {
      scale = parseFloat(match[1]);
    }
  }

  const x = rect.left + data.clientX * scale;
  const y = rect.top + data.clientY * scale;

  import('./context-menu.js').then(({ openPreviewContextMenu }) => {
    openPreviewContextMenu(x, y, panel);
  });
}

function isSameOriginIframeDocumentAvailable(iframe) {
  try {
    if (iframe.src && new URL(iframe.src, window.location.href).origin !== window.location.origin) {
      return false;
    }
    return Boolean(iframe.contentDocument || iframe.contentWindow?.document);
  } catch (err) {
    return false;
  }
}

function attachLocalDevelopmentContextMenuFallback(iframe, panel) {
  if (!isLocalDevelopmentOrigin()) return;

  iframe.addEventListener('load', () => {
    if (!isSameOriginIframeDocumentAvailable(iframe)) return;

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      const rect = iframe.getBoundingClientRect();
      panel.dispatchEvent(new MouseEvent('contextmenu', {
        bubbles: true,
        cancelable: true,
        clientX: rect.left + e.clientX,
        clientY: rect.top + e.clientY
      }));
    });
  });
}

/**
 * Bootstraps the ResizeObservers and Iframe load trackers to automate scaling updates.
 */
export function initPreviewPresets(services = {}) {
  previewPresetServices = {
    ...previewPresetServices,
    ...services
  };

  restoreCurrentState();

  const panel = refs.panelPreview;
  if (!panel) return;

  const container = panel.querySelector('.iframe-container');
  if (!container) return;

  updatePreviewScale();

  // Auto-recalculate scaling when the Preview panel is resized or dragged
  const resizeObserver = new ResizeObserver(() => {
    if (currentState.mode !== 'fit') {
      schedulePreviewScaleUpdate();
    }
  });
  resizeObserver.observe(container);

  // Sync scale ratio when window resizing occurs
  window.addEventListener('resize', () => {
    if (currentState.mode !== 'fit') {
      schedulePreviewScaleUpdate();
    }
  });

  // Production preview context menus use the preview-contextmenu postMessage path
  // from the generated document runtime. This direct document fallback is kept
  // only for the explicitly downgraded same-origin local development sandbox.
  const iframe = refs.previewIframe;
  if (iframe) {
    attachLocalDevelopmentContextMenuFallback(iframe, panel);
  }
}
